import React, {Fragment, useEffect, useRef, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import Animated, {SlideInRight, SlideOutLeft} from 'react-native-reanimated';
import {faArrowLeftLong} from '@fortawesome/free-solid-svg-icons/faArrowLeftLong';
import {faCheck} from '@fortawesome/free-solid-svg-icons/faCheck';
import {faChevronRight} from '@fortawesome/free-solid-svg-icons/faChevronRight';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {observer} from 'mobx-react-lite';

import {MAX_PRICE, MIN_PRICE} from '../stores/filterStore';
import {useStores} from '../stores/use-stores';
import theme from '../style/theme';

import SliderComponent from './slider/SliderComponent';
import Text from './ui-controls/Text';

const SINGLE = 'SINGLE';
const SLIDER = 'SLIDER';
const SUB_SCREEN = 'SUB-SCREEN';

const FILTER_MODE = 'FILTER_MODE';
const SUB_MODE = 'SUB_MODE';

// Be Aware! This filter panel allows for multi-select on subfilters.
// The MFL API does not support this yet (it ANDs all subfilters rather than
// ORing subfilters which have the same facet group)
// E.g. brand:Carlton, brand:Coopers searches for products that exist in both
// brands rather than products which are in either brand.
const RangeFilterOptions = observer(() => {
  const {rangeStore, globalStore, filterStore} = useStores();
  const [mode, setMode] = useState(FILTER_MODE);

  const sliderRef = useRef();

  // Keep watch of any changes to the filters and automagically build
  // our filter list.
  useEffect(() => {
    const newFilters = [];

    if (filterStore.specialsSelected) {
      newFilters.push(filterStore.specialsSelected.query);
    }

    if (filterStore.priceSelected) {
      newFilters.push(filterStore.priceSelected.query);
    }

    const subFilterValues = Object.values(filterStore.subFilters);
    subFilterValues.forEach(filterSet => {
      filterSet.forEach(filter => newFilters.push(filter.query));
    });
    filterStore.setFilters(newFilters);
  }, [
    filterStore.specialsSelected,
    filterStore.priceSelected,
    filterStore.subFilters,
    filterStore,
  ]);

  const clearFilters = () => {
    // Reset the current values in the slider as it maintains its own state.
    sliderRef.current.setValues(MIN_PRICE, MAX_PRICE);
    filterStore.clearFilters();
    globalStore.setFiltersVisible(false);
  };

  const applyFilters = () => {
    filterStore.filtersChanging();
    rangeStore.setSelectedFilterOptions(filterStore.currentFilters);
    globalStore.setFiltersVisible(false);
  };

  const renderApplyClearButton = () => {
    let apply = false;
    let clear = false;

    if (
      !filterStore.previousFilters?.length &&
      filterStore.currentFilters?.length
    ) {
      apply = true;
      clear = false;
    } else if (
      !filterStore.previousFilters?.length &&
      !filterStore.currentFilters?.length
    ) {
      apply = false;
      clear = false;
    } else if (
      filterStore.previousFilters?.length &&
      !filterStore.currentFilters?.length
    ) {
      apply = false;
      clear = true;
    } else {
      if (
        filterStore.previousFilters?.length !==
        filterStore.currentFilters?.length
      ) {
        // Sets are different sizes, so must be different: apply.
        apply = true;
        clear = false;
      } else {
        // Sets are the same size, make sure that the every element of the new filters
        // appear in the previously applied filters.
        let differs = false;
        let currIt = filterStore.currentFilters.values();
        let current = currIt.next();

        while (!current.done && !differs) {
          if (!filterStore.previousFilters.includes(current.value)) {
            differs = true;
          }
          current = currIt.next();
        }
        if (differs) {
          apply = true;
          clear = false;
        } else {
          apply = false;
          clear = true;
        }
      }
    }
    if (apply) {
      return (
        <Pressable onPress={() => applyFilters()} style={styles.button}>
          <Text style={styles.buttonText}>APPLY FILTERS</Text>
        </Pressable>
      );
    } else if (clear) {
      return (
        <Pressable
          onPress={() => clearFilters()}
          style={[styles.button, styles.buttonInverted]}>
          <Text style={[styles.buttonText, styles.buttonTextInverted]}>
            CLEAR FILTERS
          </Text>
        </Pressable>
      );
    } else {
      return null;
    }
  };

  const showSub = item => {
    filterStore.setCurrentParentFilter(item);
    setMode(SUB_MODE);
  };

  const renderSeparator = (index, optionLength) => {
    return index >= optionLength - 1 ? null : <View style={styles.separator} />;
  };

  const renderSingle = (item, action, selected, index = 0) => {
    return (
      <Pressable onPress={action} key={item.name + '-' + index}>
        <View style={[styles.plainRow, styles.rowPadding]}>
          <View style={{flexDirection: 'row', alignItems: 'center'}}>
            <View style={{width: 12}}>
              {selected ? (
                <FontAwesomeIcon
                  icon={faCheck}
                  size={12}
                  style={{color: theme.colour.icon.general.primary}}
                />
              ) : null}
            </View>

            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              style={[
                styles.plainText,
                selected ? styles.plainTextSelected : null,
              ]}>
              {item.name}
            </Text>
          </View>
        </View>
      </Pressable>
    );
  };

  const renderSpecialsSingle = (item, index) => {
    return renderSingle(
      item,
      () => filterStore.setSpecialsFilter(item),
      filterStore.specialsSelected,
      index,
      true,
    );
  };

  const renderSubSingle = (item, index) => {
    return renderSingle(
      item,
      () => filterStore.selectSubFilter(item),
      filterStore.subFilters[filterStore.currentParentFilter.name]?.has(item),
      index,
      false,
    );
  };

  const getSubParentSelectedText = item => {
    if (filterStore.subFilters[item.name]?.size === 1) {
      const [first] = filterStore.subFilters[item.name];
      return first.name;
    } else {
      return 'MULTIPLE';
    }
  };

  const renderSubParent = item => {
    return (
      <Pressable onPress={() => showSub(item)} key={item.name}>
        <View style={[styles.plainRow, styles.rowPadding]}>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              flex: 1,
              alignItems: 'center',
            }}>
            <View style={{flexDirection: 'row', alignItems: 'center'}}>
              <View style={{width: 12}}>
                {filterStore.subFilters[item.name]?.size > 0 && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    size={12}
                    style={{color: theme.colour.icon.general.primary}}
                  />
                )}
              </View>
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                style={[
                  styles.plainText,
                  filterStore.subFilters[item.name]?.size > 0
                    ? styles.plainTextSelected
                    : null,
                ]}>
                {item.name}
              </Text>
            </View>
            {filterStore.subFilters[item.name]?.size > 0 && (
              <Text style={styles.subFilterName}>
                {getSubParentSelectedText(item)}
              </Text>
            )}
          </View>
          <FontAwesomeIcon
            icon={faChevronRight}
            size={12}
            style={{color: 'lightgrey', alignSelf: 'center'}}
          />
        </View>
      </Pressable>
    );
  };

  // The main list for Brands/Countries/Origins
  const renderSubFilterOptions = () => {
    return (
      <Animated.View
        entering={SlideInRight}
        exiting={SlideOutLeft}
        style={styles.container}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
            marginTop: 12,
            width: '100%',
          }}>
          <Pressable
            onPress={() => {
              filterStore.setCurrentParentFilter(null);
              setMode(FILTER_MODE);
            }}
            style={{
              width: '100%',
              alignItems: 'center',
              flexDirection: 'row',
              justifyContent: 'center',
            }}>
            <View
              style={{
                position: 'absolute',
                left: 0,
                height: '100%',
                justifyContent: 'center',
              }}>
              <FontAwesomeIcon
                icon={faArrowLeftLong}
                size={12}
                style={{
                  color: theme.colour.icon.general.primary,
                  marginLeft: 8,
                }}
              />
            </View>
            <Text style={[styles.headingText, {textTransform: 'uppercase'}]}>
              {filterStore.currentParentFilter.name}
            </Text>
            {filterStore.subFilters[filterStore.currentParentFilter.name]
              ?.size > 0 && (
              <View style={{position: 'absolute', right: 2}}>
                <Pressable
                  onPress={() => {
                    filterStore.clearSubFilters();
                  }}
                  style={{
                    borderWidth: 1,
                    borderColor: theme.colour.border.general.primary,
                    borderRadius: 4,
                    paddingVertical: 2,
                    paddingHorizontal: 4,
                  }}>
                  <Text
                    style={{
                      color: theme.colour.text.heading.primary,
                      fontSize: 12,
                    }}>
                    CLEAR
                  </Text>
                </Pressable>
              </View>
            )}
          </Pressable>
        </View>
        <FlatList
          data={filterStore.currentParentFilter.subs}
          renderItem={({item, index}) => renderSubSingle(item, index)}
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
        />
      </Animated.View>
    );
  };

  const renderFilterOptions = (data = filterStore.filterOptions) => {
    return (
      <Animated.View
        entering={SlideInRight}
        exiting={SlideOutLeft}
        style={styles.container}>
        <ScrollView>
          {data?.map((item, index) => {
            if (SINGLE === item.type) {
              return (
                <Fragment key={item.name}>
                  {renderSpecialsSingle(item)}
                  {renderSeparator(index, data.length)}
                </Fragment>
              );
            } else if (SLIDER === item.type) {
              return (
                <Fragment key={item.name}>
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                    <View style={{flexDirection: 'row', alignItems: 'center'}}>
                      <View style={{width: 12}}>
                        {filterStore.priceSelected ? (
                          <FontAwesomeIcon
                            icon={faCheck}
                            size={12}
                            style={{color: theme.colour.icon.general.primary}}
                          />
                        ) : null}
                      </View>
                      <Text
                        numberOfLines={1}
                        ellipsizeMode="tail"
                        style={[
                          styles.plainText,
                          filterStore.priceSelected
                            ? styles.plainTextSelected
                            : null,
                        ]}>
                        {item.name}
                      </Text>
                    </View>
                    {!filterStore.priceSelected && (
                      <Text style={[styles.plainText, styles.plainInfoText]}>
                        (All)
                      </Text>
                    )}
                    {filterStore.priceSelected ? (
                      <Text
                        style={[
                          styles.plainText,
                          filterStore.priceSelected
                            ? styles.plainTextSelected
                            : null,
                          styles.plainInfoText,
                        ]}>
                        (${filterStore.low} - ${filterStore.high})
                      </Text>
                    ) : null}
                  </View>
                  <SliderComponent
                    min={MIN_PRICE}
                    max={MAX_PRICE}
                    initialLow={filterStore.low}
                    initialHigh={filterStore.high}
                    returnCallback={(low, high) =>
                      filterStore.setPriceFilter(low, high, item)
                    }
                    ref={sliderRef}
                  />
                  {renderSeparator(index, data.length)}
                </Fragment>
              );
            } else if (SUB_SCREEN === item.type) {
              // Brands/Countries/Origins
              return (
                <Fragment key={item.name}>
                  {renderSubParent(item)}
                  {renderSeparator(index, data.length)}
                </Fragment>
              );
            }
          })}
          {renderApplyClearButton()}
        </ScrollView>
      </Animated.View>
    );
  };
  return (
    <Animated.View
      entering={SlideInRight}
      exiting={SlideOutLeft}
      style={[styles.container]}>
      {!filterStore.filterOptions && (
        <Animated.View
          entering={SlideInRight}
          exiting={SlideOutLeft}
          style={{marginTop: 16}}>
          <ActivityIndicator
            size="small"
            color={theme.colour.activityIndicator.primary}
          />
        </Animated.View>
      )}
      {FILTER_MODE === mode && renderFilterOptions()}
      {SUB_MODE === mode && renderSubFilterOptions()}
    </Animated.View>
  );
});

export default RangeFilterOptions;

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingLeft: 0,
    // marginBottom: 16,
    backgroundColor: 'white',
    flex: 1,
  },
  headingText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colour.text.heading.primary,
  },
  button: {
    backgroundColor: theme.colour.background.button.primary,
    width: '80%',
    borderRadius: 4,
    marginTop: 4,
    padding: 8,
    alignItems: 'center',
    alignSelf: 'center',
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
  },
  buttonInverted: {
    backgroundColor: 'white',
    borderColor: theme.colour.border.general.primary,
    borderWidth: 1,
  },
  buttonTextInverted: {
    color: theme.colour.text.heading.primary,
  },
  // Segment list
  scrollView: {
    width: '100%',
  },
  plainRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rowPadding: {
    paddingVertical: 8,
  },

  radioButtonRow: {
    flexDirection: 'row',
    paddingLeft: 16,
    marginVertical: 4,
  },
  chevronText: {
    fontSize: 16,
  },
  chevron: {
    marginLeft: 8,
    marginRight: 4,
  },
  plainText: {
    fontSize: 16,
    marginLeft: 8,
    textTransform: 'capitalize',
  },
  plainTextSelected: {
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
  },
  plainInfoText: {
    fontSize: 16,
    fontWeight: '300',
  },
  subFilterName: {
    color: theme.colour.text.heading.primary,
    fontSize: 12,
    fontWeight: '400',
    alignSelf: 'center',
  },
  separator: {
    marginVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: 'lightgrey',
  },
});
